import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import CopilotBlockRenderer from "./CopilotBlocks";
import CopilotMarkdown from "./CopilotMarkdown";
import { copyCopilotText } from "./CopilotComposer";
import { useCopilotShell } from "./CopilotShellContext";
import {
  filterDuplicateActions,
  getAssistantDisplayContent,
  groupBlocksForDisplay,
  pick,
  WORKFLOW_LABELS,
} from "./copilotUtils";
import { usePostDataMutation } from "../../app/services/commonApi";
import Toast from "@moltech/molui/toast";

const CopilotTurnFeedback = ({ message }) => {
  const { threads } = useCopilotShell();
  const [postData] = usePostDataMutation();
  const [rating, setRating] = useState(null);
  const [commentOpen, setCommentOpen] = useState(false);
  const [comment, setComment] = useState("");
  const logId = pick(message, "aiExecutionLogId", "AIExecutionLogId");
  const conversationId = threads?.activeThread?.conversationId;

  const submit = async (nextRating, nextComment) => {
    try {
      await postData({
        url: "/AIExecution/SubmitFeedback",
        data: {
          aiExecutionLogId: logId ? Number(logId) : null,
          conversationId: conversationId || null,
          rating: nextRating,
          comment: nextComment || null,
        },
      }).unwrap();
      setRating(nextRating);
      Toast.success("Thanks for the feedback.");
    } catch (err) {
      Toast.error(err?.data?.message || err?.message || "Could not save feedback.");
    }
  };

  return (
    <div className="copilot-turn-feedback">
      <button
        type="button"
        className={`copilot-turn-feedback__btn${rating === 1 ? " is-active" : ""}`}
        title="Helpful"
        onClick={() => submit(1)}
      >
        <i className="pi pi-thumbs-up" />
      </button>
      <button
        type="button"
        className={`copilot-turn-feedback__btn${rating === -1 ? " is-active" : ""}`}
        title="Not helpful"
        onClick={() => {
          setCommentOpen(true);
          submit(-1);
        }}
      >
        <i className="pi pi-thumbs-down" />
      </button>
      {commentOpen ? (
        <form
          className="copilot-turn-feedback__comment"
          onSubmit={(e) => {
            e.preventDefault();
            submit(-1, comment);
            setCommentOpen(false);
          }}
        >
          <input
            className="p-inputtext p-component p-inputtext-sm"
            value={comment}
            maxLength={500}
            placeholder="Optional comment"
            onChange={(e) => setComment(e.target.value)}
          />
          <Button type="submit" label="Send" className="p-button-sm p-button-text" />
        </form>
      ) : null}
    </div>
  );
};

CopilotTurnFeedback.propTypes = {
  message: PropTypes.object.isRequired,
};

const LOADING_STATUSES = [
  "Reviewing your request",
  "Processing",
  "Getting response",
];

const GROUP_TITLES = {
  jobopeningcard: "Published openings",
  candidatecard: "Candidates",
  interviewercard: "Interviewers",
  slotcard: "Slots",
};

const GROUPED_BLOCK_KINDS = Object.keys(GROUP_TITLES);

const getBlockGroupTitle = (group) =>
  pick(group.blocks[0], "title", "Title") || GROUP_TITLES[group.kind] || null;

const getMessageKey = (message) =>
  pick(message, "id", "Id", "messageId", "MessageId") ||
  `${message.role}-${String(message.content || "").slice(0, 32)}-${String(message.errorText || "").length}`;

const getActionKey = (action) =>
  pick(action, "id", "Id", "label", "Label") || JSON.stringify(action);

const getRecipientKey = (recipient) =>
  String(
    recipient.applicantId ||
      recipient.ApplicantId ||
      recipient.applicantEmail ||
      recipient.ApplicantEmail ||
      recipient.applicantName ||
      recipient.ApplicantName ||
      JSON.stringify(recipient)
  );

const hasConfirmationBlock = (blocks = []) =>
  blocks.some(
    (block) => String(pick(block, "type", "Type") || "").toLowerCase() === "confirmation"
  );

const CopilotBlockGroup = ({
  group,
  onNavigate,
  onAction,
  workflow,
  formDisabled,
  onFormSave,
  onFormCancel,
  onFormRefresh,
}) => {
  const [expanded, setExpanded] = useState(false);
  const isGroupedList = GROUPED_BLOCK_KINDS.includes(group.kind);
  const count = group.blocks.length;
  const title = getBlockGroupTitle(group);

  if (!isGroupedList) {
    return (
      <CopilotBlockRenderer
        block={group.blocks[0]}
        onNavigate={onNavigate}
        onAction={onAction}
        workflow={workflow}
        formDisabled={formDisabled}
        onFormSave={onFormSave}
        onFormCancel={onFormCancel}
        onFormRefresh={onFormRefresh}
      />
    );
  }

  const preview = 10;
  const showAll = expanded || count <= preview;
  const visible = showAll ? group.blocks : group.blocks.slice(0, preview);

  return (
    <div className="copilot-block-group">
      <div className="copilot-block-group__header">
        <span className="copilot-block-group__title">{title}</span>
        <span className="copilot-block-group__count">{count}</span>
      </div>
      <div className="copilot-block-group__list">
        {visible.map((block) => (
          <CopilotBlockRenderer
            key={`${group.key}-${getMessageKey(block)}`}
            block={block}
            onNavigate={onNavigate}
            onAction={onAction}
            compact
          />
        ))}
      </div>
      {count > preview ? (
        <button
          type="button"
          className="copilot-block-group__more"
          onClick={() => setExpanded((v) => !v)}
        >
          {showAll ? "Show less" : `Show ${count - preview} more`}
        </button>
      ) : null}
    </div>
  );
};

CopilotBlockGroup.propTypes = {
  group: PropTypes.object.isRequired,
  onNavigate: PropTypes.func,
  onAction: PropTypes.func,
  workflow: PropTypes.object,
  formDisabled: PropTypes.bool,
  onFormSave: PropTypes.func,
  onFormCancel: PropTypes.func,
  onFormRefresh: PropTypes.func,
};

const CopilotMessageHeader = ({ isAssistant }) => (
  <div className="copilot-chat__bubble-head">
    {isAssistant ? (
      <span className="copilot-chat__avatar copilot-chat__avatar--ai">
        <i className="pi pi-sparkles" />
      </span>
    ) : (
      <span className="copilot-chat__avatar copilot-chat__avatar--user">
        <i className="pi pi-user" />
      </span>
    )}
    <span className="copilot-chat__sender">
      {isAssistant ? "MolAssist" : "You"}
    </span>
  </div>
);

CopilotMessageHeader.propTypes = {
  isAssistant: PropTypes.bool.isRequired,
};

const CopilotMessageContent = ({ isAssistant, displayContent, onDraftPrompt }) => {
  if (!displayContent) {
    return null;
  }

  return (
    <div className="copilot-chat__content">
      {isAssistant ? (
        <CopilotMarkdown text={displayContent} onBulletClick={onDraftPrompt} />
      ) : (
        displayContent
      )}
    </div>
  );
};

CopilotMessageContent.propTypes = {
  isAssistant: PropTypes.bool.isRequired,
  displayContent: PropTypes.node,
  onDraftPrompt: PropTypes.func,
};

const CopilotMessageBlocks = ({
  blockGroups,
  activeWorkflow,
  messageWorkflow,
  isLoading,
  isLastAssistant,
  onAction,
  onNavigate,
  onCancelWorkflow,
  onFormSave,
  onFormRefresh,
}) => {
  if (!blockGroups.length) {
    return null;
  }

  return (
    <div className="copilot-chat__blocks">
      {blockGroups.map((group) => (
        <CopilotBlockGroup
          key={group.key}
          group={group}
          onNavigate={onNavigate}
          onAction={onAction}
          workflow={activeWorkflow || messageWorkflow}
          formDisabled={isLoading || !isLastAssistant}
          onFormSave={onFormSave}
          onFormCancel={onCancelWorkflow}
          onFormRefresh={onFormRefresh}
        />
      ))}
    </div>
  );
};

CopilotMessageBlocks.propTypes = {
  blockGroups: PropTypes.array.isRequired,
  activeWorkflow: PropTypes.object,
  messageWorkflow: PropTypes.object,
  isLoading: PropTypes.bool,
  isLastAssistant: PropTypes.bool,
  onAction: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onCancelWorkflow: PropTypes.func.isRequired,
  onFormSave: PropTypes.func,
  onFormRefresh: PropTypes.func,
};

const PendingConfirmationBlock = ({ pendingConfirmation, onAction }) => {
  if (!pendingConfirmation) {
    return null;
  }

  return (
    <div className="copilot-chat__blocks">
      <CopilotBlockRenderer
        block={{
          type: "confirmation",
          title: "Action preview",
          markdown: pendingConfirmation,
        }}
        onAction={onAction}
      />
    </div>
  );
};

PendingConfirmationBlock.propTypes = {
  pendingConfirmation: PropTypes.node,
  onAction: PropTypes.func.isRequired,
};

const CopilotEmailDraft = ({ message, onReviewEmail }) => {
  if (!message.draftEmailText) {
    return null;
  }

  const hasRecipients = Array.isArray(message.draftEmailRecipients) &&
    message.draftEmailRecipients.length > 0;

  return (
    <div className="copilot-email-draft">
      <div className="copilot-email-draft__header">
        <strong>Email draft (copy only)</strong>
        <Button
          type="button"
          label="Copy"
          icon="pi pi-copy"
          className="p-button-sm p-button-text"
          onClick={() => copyCopilotText(message.draftEmailText)}
        />
        {hasRecipients ? (
          <Button
            type="button"
            label="Review & Send"
            icon="pi pi-send"
            className="p-button-sm p-button-text"
            onClick={() => onReviewEmail(message)}
          />
        ) : null}
      </div>
      <pre className="copilot-email-draft__body">{message.draftEmailText}</pre>
    </div>
  );
};

CopilotEmailDraft.propTypes = {
  message: PropTypes.object.isRequired,
  onReviewEmail: PropTypes.func.isRequired,
};

const CopilotActionChips = ({ actions, onAction }) => {
  if (!actions?.length) {
    return null;
  }

  return (
    <div className="copilot-chat__actions">
      {actions.slice(0, 4).map((action) => (
        <button
          key={getActionKey(action)}
          type="button"
          className="copilot-action-chip"
          onClick={() => onAction(action)}
        >
          {pick(action, "label", "Label") || "Open"}
        </button>
      ))}
    </div>
  );
};

CopilotActionChips.propTypes = {
  actions: PropTypes.array,
  onAction: PropTypes.func.isRequired,
};

const CopilotMessageItem = ({
  message,
  isLastAssistant,
  activeWorkflow,
  debugMode,
  isLoading,
  onAction,
  onNavigate,
  onCancelWorkflow,
  onDraftPrompt,
  onFormSave,
  onFormRefresh,
  onReviewEmail,
}) => {
  const isAssistant = message.role === "assistant";
  const displayContent = isAssistant
    ? getAssistantDisplayContent(message)
    : message.content;
  const blockGroups = isAssistant ? groupBlocksForDisplay(message.blocks) : [];
  const actions = isAssistant
    ? filterDuplicateActions(message.suggestedActions, message.blocks)
    : message.suggestedActions;
  const pendingConfirmation = pick(message, "pendingConfirmation", "PendingConfirmation");
  const shouldShowConfirmation = isAssistant &&
    pendingConfirmation &&
    !hasConfirmationBlock(message.blocks);

  return (
    <article
      key={getMessageKey(message)}
      className={`copilot-chat__bubble copilot-chat__bubble--${message.role}${message.isError ? " copilot-chat__bubble--error" : ""}`}
    >
      <CopilotMessageHeader isAssistant={isAssistant} />
      <CopilotMessageContent
        isAssistant={isAssistant}
        displayContent={displayContent}
        onDraftPrompt={onDraftPrompt}
      />

      {debugMode && isAssistant && message.errorText ? (
        <pre className="copilot-chat__error-detail">{message.errorText}</pre>
      ) : null}

      <CopilotMessageBlocks
        blockGroups={blockGroups}
        activeWorkflow={activeWorkflow}
        messageWorkflow={message.workflow}
        isLoading={isLoading}
        isLastAssistant={isLastAssistant}
        onAction={onAction}
        onNavigate={onNavigate}
        onCancelWorkflow={onCancelWorkflow}
        onFormSave={onFormSave}
        onFormRefresh={onFormRefresh}
      />

      <PendingConfirmationBlock
        pendingConfirmation={shouldShowConfirmation ? pendingConfirmation : null}
        onAction={onAction}
      />

      {isAssistant && !message.isError ? <CopilotTurnFeedback message={message} /> : null}
      <CopilotEmailDraft message={message} onReviewEmail={onReviewEmail} />
      <CopilotActionChips actions={actions} onAction={onAction} />
    </article>
  );
};

CopilotMessageItem.propTypes = {
  message: PropTypes.object.isRequired,
  isLastAssistant: PropTypes.bool,
  activeWorkflow: PropTypes.object,
  debugMode: PropTypes.bool,
  isLoading: PropTypes.bool,
  onAction: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onCancelWorkflow: PropTypes.func.isRequired,
  onDraftPrompt: PropTypes.func,
  onFormSave: PropTypes.func,
  onFormRefresh: PropTypes.func,
  onReviewEmail: PropTypes.func.isRequired,
};

const CopilotMessageList = ({
  messages,
  isLoading,
  activeWorkflow,
  onAction,
  onNavigate,
  onCancelWorkflow,
  onDraftPrompt,
  onFormSave,
  onFormRefresh,
  onSendEmailDraft,
  isSendingEmailDraft,
}) => {
  const { debugMode } = useCopilotShell();
  const bottomRef = useRef(null);
  const [statusPhase, setStatusPhase] = useState(0);
  const [emailReviewOpen, setEmailReviewOpen] = useState(false);
  const [emailReviewDraft, setEmailReviewDraft] = useState({ subject: "", recipients: [] });
  const [emailPreviewIndex, setEmailPreviewIndex] = useState(0);
  const lastMessage = messages[messages.length - 1];
  let lastMessageFingerprint = "";
  if (lastMessage) {
    const lastMessageBlockCount = Array.isArray(lastMessage.blocks)
      ? lastMessage.blocks.length
      : 0;
    lastMessageFingerprint = `${lastMessage.role}:${String(lastMessage.content || "").length}:${String(lastMessage.errorText || "").length}:${lastMessageBlockCount}`;
  }

  useEffect(() => {
    if (!isLoading) {
      setStatusPhase(0);
      return undefined;
    }

    setStatusPhase(0);
    const processingTimer = window.setTimeout(() => setStatusPhase(1), 1200);
    const gettingResponseTimer = window.setTimeout(() => setStatusPhase(2), 2800);

    return () => {
      window.clearTimeout(processingTimer);
      window.clearTimeout(gettingResponseTimer);
    };
  }, [isLoading]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, isLoading, lastMessageFingerprint]);

  const lastAssistantIndex = [...messages]
    .map((m, i) => ({ m, i }))
    .reverse()
    .find((x) => x.m.role === "assistant")?.i;
  const lastAssistant =
    lastAssistantIndex != null ? messages[lastAssistantIndex] : null;
  const workflowType =
    pick(activeWorkflow, "type", "Type") || pick(lastAssistant?.workflow, "type", "Type");
  const workflowStep =
    pick(activeWorkflow, "step", "Step") || pick(lastAssistant?.workflow, "step", "Step");
  const openEmailReview = (message) => {
    setEmailReviewDraft({
      subject: message.draftEmailSubject || "",
      recipients: message.draftEmailRecipients || [],
    });
    setEmailPreviewIndex(0);
    setEmailReviewOpen(true);
  };

  return (
    <div className="copilot-chat">
      {activeWorkflow ? (
        <output className="copilot-workflow-bar">
          <span className="copilot-workflow-bar__chip">
            {WORKFLOW_LABELS[workflowType] || workflowType}
          </span>
          {workflowStep ? (
            <span className="copilot-workflow-bar__step">Step: {workflowStep}</span>
          ) : null}
          <Button
            type="button"
            label="Cancel"
            className="p-button-text p-button-sm"
            disabled={isLoading}
            onClick={onCancelWorkflow}
          />
        </output>
      ) : null}

      <div className="copilot-chat__messages">
        {messages.map((message) => (
          <CopilotMessageItem
            key={getMessageKey(message)}
            message={message}
            isLastAssistant={message === lastAssistant}
            activeWorkflow={activeWorkflow}
            debugMode={debugMode}
            isLoading={isLoading}
            onAction={onAction}
            onNavigate={onNavigate}
            onCancelWorkflow={onCancelWorkflow}
            onDraftPrompt={onDraftPrompt}
            onFormSave={onFormSave}
            onFormRefresh={onFormRefresh}
            onReviewEmail={openEmailReview}
          />
        ))}

        {isLoading ? (
          <div className="copilot-chat__typing">
            <span className="copilot-chat__avatar copilot-chat__avatar--ai">
              <i className="pi pi-sparkles" />
            </span>
            <span className="copilot-chat__typing-dots">
              <span />
              <span />
              <span />
            </span>
            <span className="copilot-chat__typing-status" aria-live="polite">
              {LOADING_STATUSES[statusPhase]}
            </span>
          </div>
        ) : null}
        <div ref={bottomRef} className="copilot-chat__scroll-anchor" aria-hidden="true" />
        <Dialog
          visible={emailReviewOpen}
          onHide={() => setEmailReviewOpen(false)}
          header="Review & Send email shortlist"
          modal
          style={{ width: "900px", maxWidth: "95vw" }}
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <Button
                type="button"
                label="Cancel"
                className="p-button-text"
                onClick={() => setEmailReviewOpen(false)}
                disabled={isSendingEmailDraft}
              />
              <Button
                type="button"
                label={isSendingEmailDraft ? "Sending..." : "Send"}
                icon="pi pi-send"
                disabled={isSendingEmailDraft || !emailReviewDraft.recipients?.length}
                onClick={async () => {
                  await onSendEmailDraft?.(emailReviewDraft);
                  setEmailReviewOpen(false);
                }}
              />
            </div>
          }
        >
          <div className="copilot-email-review">
            <div style={{ marginBottom: "0.75rem" }}>
              <strong>Subject:</strong> <span>{emailReviewDraft.subject}</span>
            </div>

            <div style={{ marginBottom: "0.75rem" }}>
              <strong>Recipients ({emailReviewDraft.recipients.length}):</strong>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
                {emailReviewDraft.recipients.map((r, idx) => (
                  <button
                    key={getRecipientKey(r)}
                    type="button"
                    className="copilot-email-review__recipient-chip"
                    onClick={() => setEmailPreviewIndex(idx)}
                    style={{
                      border:
                        idx === emailPreviewIndex ? "1px solid var(--primary-color)" : "1px solid #d0d0d0",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "6px",
                      cursor: "pointer",
                      background: idx === emailPreviewIndex ? "rgba(0,0,0,0.02)" : "transparent",
                    }}
                  >
                    {r.applicantName || r.applicantEmail || `Recipient ${idx + 1}`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <strong>Preview (recipient {emailPreviewIndex + 1}):</strong>
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {emailReviewDraft.recipients[emailPreviewIndex]?.body || ""}
              </pre>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  );
};

CopilotMessageList.propTypes = {
  messages: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  activeWorkflow: PropTypes.object,
  onAction: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onCancelWorkflow: PropTypes.func.isRequired,
  onDraftPrompt: PropTypes.func,
  onFormSave: PropTypes.func,
  onFormRefresh: PropTypes.func,
  onSendEmailDraft: PropTypes.func,
  isSendingEmailDraft: PropTypes.bool,
};

export default CopilotMessageList;
